/**
 * ============================================================================
 * VINPOP — Taste Profile Quiz for Tasting Collection
 * ============================================================================
 *
 * Independent quiz system for /collections/quizz-tasting.
 * Same 6 questions, same XYZ scoring algorithm as the original quiz,
 * but displays TASTING products instead of real wines.
 *
 * REUSES the existing #taste-profile-popup rendered by taste-profile-trigger.liquid
 * (same popup that works perfectly on /collections/quizz). Only the result
 * rendering is different: maps API wines to quizz-tasting products via productidmatch/productid.
 *
 * The quizz-tasting products have a custom.productidmatch/productid metafield that maps
 * to real wine product IDs in the API. After scoring, we map API results
 * back to quizz-tasting products.
 *
 * COMPLETELY SEPARATE from:
 *   - taste-profile-quiz.js (original quiz for /collections/quizz — guard prevents it here)
 *   - quiz-auto-popup.js (original auto-popup — guard prevents it here)
 *   - wine-recommendation-manager.js (original result renderer — guard prevents it here)
 *   - tasting-recommendations.js (wine-assistant for /collections/tasting)
 *
 * Loaded from: layout/theme.liquid (conditional on /collections/quizz-tasting)
 * Depends on:  quizz-tasting-catalog-data.liquid, taste-profile-trigger.liquid (#taste-profile-popup)
 * CSS:         taste-profile-quiz.css (shared styles)
 * ============================================================================
 */

(function() {
  'use strict';

  // Prevent double-loading
  if (window.__quizzTastingQuizLoaded) return;
  window.__quizzTastingQuizLoaded = true;

  const pathname = window.location.pathname;
  if (!pathname.includes('/collections/quizz-tasting') && !pathname.includes('/collections/respond-to-the-quiz-v2')) return;
  const useV2CollectionTheme = pathname.includes('/collections/respond-to-the-quiz-v2') || pathname.includes('/collections/quizz-tasting');

  console.log('🧪 Quizz-Tasting Quiz: Initializing...');

  // ========== STORAGE KEYS (completely separate from original quiz) ==========
  const STORAGE_KEYS = {
    QUIZ_COMPLETED: 'vinpop-quizz-tasting-completed',
    QUIZ_POPUP_SHOWN: 'vinpop-quizz-tasting-popup-shown',
    QUIZ_HINT_PENDING: 'vinpop-quizz-tasting-hint-pending',
    LAST_RESULTS: 'vinpop_quizz_tasting_results',
    LAST_RESULTS_PERSIST: 'vinpop_quizz_tasting_results_persist',
    TASTE_PROFILE: 'vinpop_quizz_tasting_profile',
    QUIZ_STATE: 'vinpop_quizz_tasting_state',
    QUIZ_ANSWERS: 'vinpop_quizz_tasting_answers'
  };

  // ========== HIDE PRICES ON ALL TASTING PAGES ==========
  (function hidePrices() {
    function hideAllPrices() {
      document.querySelectorAll('[data-product-price]').forEach(el => {
        el.style.setProperty('display', 'none', 'important');
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', hideAllPrices);
    } else {
      hideAllPrices();
    }
    const observer = new MutationObserver(() => hideAllPrices());
    observer.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 10000);
  })();

  // ========== HIJACK EXISTING QUIZ ELEMENTS ==========
  // The taste-profile-trigger.liquid section renders #taste-profile-popup on quizz-tasting too.
  // We KEEP the popup (reuse it!) but redirect the trigger button and replace the banner.
  function hijackExistingQuiz() {
    // 1. Intercept clicks on #taste-profile-trigger-btn
    //    Use capturing phase to fire BEFORE any original quiz listener
    const existingTrigger = document.getElementById('taste-profile-trigger-btn');
    if (existingTrigger) {
      existingTrigger.addEventListener('click', (e) => {
        e.stopImmediatePropagation();
        e.preventDefault();
        if (window.tastingQuiz) {
          window.tastingQuiz.openPopup();
        }
      }, true); // capture phase
      console.log('🧪 Hijacked #taste-profile-trigger-btn for tasting quiz');
    }

    // 2. Replace the banner content
    const banner = document.querySelector('.demo-mode-banner');
    if (banner) {
      const lang = (document.documentElement.lang || 'nl').toLowerCase().substring(0, 2);
      const isEN = lang === 'en';
      const tastingDone = sessionStorage.getItem(STORAGE_KEYS.QUIZ_COMPLETED) === 'true';

      if (tastingDone) {
        banner.innerHTML =
          '<div class="demo-mode-banner__title" style="margin-bottom: 0.5rem; color: #000; font-weight: bold; font-size: 1.2rem;">' +
            (isEN ? '✅ Your tasting selection is ready!' : '✅ Je proeverijselectie is klaar!') +
          '</div>' +
          '<div class="demo-mode-banner__text" style="margin-bottom: 1rem; color: #000; font-size: 0.95rem; line-height: 1.5; max-width: 600px; margin-left: auto; margin-right: auto;">' +
            (isEN
              ? '<strong>The VinPOP Difference:</strong> We matched your answers with lab-analyzed flavor profiles to find the exact tastings that fit your palate.'
              : '<strong>Het VinPOP Verschil:</strong> We hebben jouw antwoorden gematcht met laboratorium-geanalyseerde smaakprofielen om precies de proeverijen te vinden die bij jouw smaak passen.') +
          '</div>' +
          '<div style="display:flex;gap:0.75rem;justify-content:center;flex-wrap:wrap;">' +
            '<button type="button" class="push-btn" onclick="' +
              "localStorage.removeItem('vinpop-quizz-tasting-completed');" +
              "sessionStorage.removeItem('vinpop-quizz-tasting-completed');" +
              "sessionStorage.removeItem('vinpop_quizz_tasting_results');" +
              "localStorage.removeItem('vinpop_quizz_tasting_results_persist');" +
              "sessionStorage.removeItem('vinpop_quizz_tasting_profile');" +
              "sessionStorage.removeItem('vinpop_quizz_tasting_answers');" +
              "location.reload();" +
            '">' +
              '<span class="push-btn__surface">' + (isEN ? '🔄 Retake the Quiz' : '🔄 Quiz opnieuw doen') + '</span>' +
            '</button>' +
          '</div>';
      } else {
        banner.innerHTML =
          '<div class="demo-mode-banner__title" style="color: #000; font-weight: bold; font-size: 1.2rem; margin-bottom: 0.5rem;">' +
            (isEN ? '🍷 Discover your perfect wines with science, not opinions.' : '🍷 Ontdek jouw perfecte wijnen met wetenschap, niet met meningen.') +
          '</div>' +
          '<div class="demo-mode-banner__text" style="color: #000; font-size: 0.95rem; line-height: 1.5; max-width: 600px; margin-left: auto; margin-right: auto; margin-bottom: 1rem;">' +
            (isEN
              ? '<strong>The VinPOP Difference:</strong> Unlike popular wine-scanning apps or traditional shops, we don\'t rely on subjective reviews, critics, or guesswork. Our selection is <strong>purely rational</strong>. We match your answers directly with lab-analyzed flavor profiles to find the exact wines that fit your palate.'
              : '<strong>Het VinPOP Verschil:</strong> Anders dan populaire wijnscanner-apps of traditionele winkels vertrouwen wij niet op subjectieve reviews, critici of giswerk. Onze selectie is <strong>puur rationeel</strong>. We matchen jouw antwoorden direct met laboratorium-geanalyseerde smaakprofielen om precies de wijnen te vinden die bij jouw smaak passen.') +
          '</div>' +
          '<div style="display:flex;gap:0.75rem;justify-content:center;flex-wrap:wrap;">' +
            '<button type="button" class="push-btn" onclick="' +
              "localStorage.removeItem('vinpop-quizz-tasting-completed');" +
              "sessionStorage.removeItem('vinpop-quizz-tasting-completed');" +
              "sessionStorage.removeItem('vinpop_quizz_tasting_results');" +
              "localStorage.removeItem('vinpop_quizz_tasting_results_persist');" +
              "sessionStorage.removeItem('vinpop_quizz_tasting_profile');" +
              "sessionStorage.removeItem('vinpop_quizz_tasting_answers');" +
              "if(window.tastingQuiz){window.tastingQuiz.openPopup()}" +
            '">' +
              '<span class="push-btn__surface">' + (isEN ? '🧪 Start the Quiz' : '🧪 Start de Quiz') + '</span>' +
            '</button>' +
          '</div>';
      }
      console.log('🧪 Banner replaced (tastingDone=' + tastingDone + ')');
    }

    console.log('🧪 Existing quiz elements hijacked');
  }

  // ========== QUIZ CLASS ==========
  // Uses the EXISTING #taste-profile-popup from taste-profile-trigger.liquid
  // Same selectors as the working quiz on /collections/quizz
  class TastingQuiz {
    constructor() {
      this.currentQuestion = 1;
      this.totalQuestions = 6;
      this.targetX = 0;
      this.targetY = 0;
      this.targetZ = 0;
      this.colorPreference = 'both';
      this.answers = [];
      this.quizSavePromise = null;
      this.lastQuizSaveSignature = '';
      this.recommendedWines = null;
      this.isProcessing = false;
      this.fetchRequestSeq = 0;

      // Use the EXISTING popup rendered by taste-profile-trigger.liquid
      this.popup = document.getElementById('taste-profile-popup');
      if (!this.popup) {
        console.error('❌ TastingQuiz: #taste-profile-popup not found!');
        return;
      }

      // Standard selectors — same as the working quiz on /collections/quizz
      this.closeBtn = this.popup.querySelector('.taste-profile-close');
      this.overlay = this.popup.querySelector('.taste-profile-popup-overlay');
      this.progressFill = this.popup.querySelector('.taste-profile-progress-fill');
      this.progressText = this.popup.querySelector('.current-question');
      this.loadingEl = this.popup.querySelector('.taste-profile-loading');
      this.backBtn = this.popup.querySelector('.taste-profile-back-btn');
      this.revealEl = this.popup.querySelector('.taste-profile-reveal');
      this.revealBtn = this.popup.querySelector('.taste-profile-reveal-btn');

      this.init();
    }

    init() {
      if (!this.popup) return;

      // Detect inline quiz (rendered by taste-profile-quiz-inline.liquid)
      this.isInline = this.popup.classList.contains('taste-profile-popup--inline');

      // URL autostart
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('autostart') === 'true') {
        const newUrl = window.location.pathname + window.location.search.replace(/[?&]autostart=true/, '').replace(/^&/, '?');
        window.history.replaceState({}, '', newUrl);
        if (!this.isInline) {
          setTimeout(() => this.openPopup(), 500);
        }
      }

      // Event listeners
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closePopup());
      if (this.overlay) this.overlay.addEventListener('click', () => this.closePopup());
      if (this.backBtn) this.backBtn.addEventListener('click', () => this.goBack());
      if (this.revealBtn) this.revealBtn.addEventListener('click', () => this.revealResults());

      // Wire intro "Let's Go" button immediately (for inline quiz)
      const introBtn = this.popup.querySelector('.quiz-intro-start-btn');
      if (introBtn && !introBtn._wired) {
        introBtn._wired = true;
        introBtn.addEventListener('click', () => {
          this.currentQuestion = 1;
          this.updateProgress();
          this.showQuestion(1);
          this.saveQuizState();
        });
      }

      // Answer buttons — scoped to our popup using standard .taste-profile-answer
      this.popup.querySelectorAll('.taste-profile-answer').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleAnswer(e));
      });

      // ESC key
      document.addEventListener('keydown', (e) => {
        if (this.popup.style.display === 'block' && e.key === 'Escape') {
          this.closePopup();
        }
      });

      // Check for in-progress quiz to restore (e.g. language switch)
      const savedState = sessionStorage.getItem(STORAGE_KEYS.QUIZ_STATE);
      if (savedState && !sessionStorage.getItem(STORAGE_KEYS.LAST_RESULTS)) {
        console.log('🔄 Restoring tasting quiz state (language switch)');
        this.restoreQuizState(JSON.parse(savedState));
      }

      // Restore answers for tweak panel when quiz is already completed (e.g. language switch or page reload)
      const savedAnswers = sessionStorage.getItem(STORAGE_KEYS.QUIZ_ANSWERS);
      if (savedAnswers && sessionStorage.getItem(STORAGE_KEYS.LAST_RESULTS)) {
        try {
          this.answers = JSON.parse(savedAnswers);
          this.targetX = 0; this.targetY = 0; this.targetZ = 0;
          this.colorPreference = 'both';
          this.answers.forEach(a => {
            if (a.color) { this.colorPreference = a.color; }
            else { this.targetX += (a.x || 0); this.targetY += (a.y || 0); this.targetZ += (a.z || 0); }
          });
          console.log('🔧 Restored answers for tweak panel:', this.answers.length, 'answers, color=', this.colorPreference);
        } catch (e) {
          console.warn('⚠️ Failed to restore tasting answers:', e);
        }
      }

      console.log('✅ TastingQuiz: Initialized with #taste-profile-popup (inline=' + this.isInline + ')');
    }

    openPopup() {
      // Don't reset if quiz is already open and user has started answering
      if (this.isInline) {
        // Inline mode: just make sure it's visible and scroll to it
        this.popup.style.display = '';
        this.reset();
        var headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-group-height')) || 120;
        var top = this.popup.getBoundingClientRect().top + window.pageYOffset - headerH - 10;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        return;
      }
      if (this.popup.style.display === 'block' && this.currentQuestion > 0) {
        console.log('🧪 Quiz already in progress, ignoring openPopup()');
        return;
      }
      this.popup.style.display = 'block';
      document.body.style.overflow = 'hidden';
      this.reset();
    }

    closePopup() {
      if (this.isInline) {
        // Inline mode: hide the quiz section, don't block body scroll
        this.popup.style.display = 'none';
        document.body.style.overflow = '';
      } else {
        this.popup.style.display = 'none';
        document.body.style.overflow = '';
      }
    }

    reset() {
      this.currentQuestion = 0;
      this.targetX = 0;
      this.targetY = 0;
      this.targetZ = 0;
      this.colorPreference = 'both';
      this.answers = [];
      this.isProcessing = false;
      sessionStorage.removeItem(STORAGE_KEYS.QUIZ_STATE);

      if (this.revealEl) this.revealEl.style.display = 'none';
      if (this.loadingEl) this.loadingEl.style.display = 'none';

      // First-time users: skip intro, go straight to Q1
      // Returning users (retaking): show intro step
      var done = sessionStorage.getItem(STORAGE_KEYS.QUIZ_COMPLETED) === 'true';
      var startStep = done ? 0 : 1;
      this.currentQuestion = startStep;
      this.updateProgress();
      this.showQuestion(startStep);

      // Wire up intro "Start" button
      const introBtn = this.popup.querySelector('.quiz-intro-start-btn');
      if (introBtn && !introBtn._wired) {
        introBtn._wired = true;
        introBtn.addEventListener('click', () => {
          this.currentQuestion = 1;
          this.updateProgress();
          this.showQuestion(1);
        });
      }
    }

    handleAnswer(e) {
      if (this.isProcessing) return;
      this.isProcessing = true;

      window.VinPopTracking?.trackQuizStarted({ quiz_type: 'quizz-tasting' });

      const button = e.currentTarget;
      const allButtons = Array.from(button.parentElement.querySelectorAll('.taste-profile-answer'));
      const answerIndex = allButtons.indexOf(button);

      const color = button.dataset.color;
      if (color) {
        this.colorPreference = color;
        this.answers.push({ question: this.currentQuestion, color, choice: answerIndex });
      } else {
        const x = parseFloat(button.dataset.x);
        const y = parseFloat(button.dataset.y);
        const z = parseFloat(button.dataset.z) || 0;
        this.targetX += x;
        this.targetY += y;
        this.targetZ += z;
        this.answers.push({ question: this.currentQuestion, x, y, z, choice: answerIndex });
      }

      button.classList.add('selected');

      setTimeout(() => {
        if (this.currentQuestion < this.totalQuestions) {
          this.currentQuestion++;
          this.updateProgress();
          this.showQuestion(this.currentQuestion);
          button.classList.remove('selected');
          this.saveQuizState();
        } else {
          window.VinPopTracking?.trackQuizCompleted({ quiz_type: 'quizz-tasting' });
          sessionStorage.removeItem(STORAGE_KEYS.QUIZ_STATE);
          this.ensureQuizSavedToApi()
            .catch(error => {
              console.warn('TastingQuiz: could not save quiz answers:', error);
            })
            .finally(() => this.fetchResults());
        }
        // Re-enable after animation
        setTimeout(() => { this.isProcessing = false; }, 350);
      }, 250);
    }

    showQuestion(questionNumber) {
      // Use standard selectors scoped to popup
      let visibleEl = null;
      this.popup.querySelectorAll('.taste-profile-question').forEach(q => {
        const qNum = parseInt(q.dataset.question);
        const show = qNum === questionNumber;
        q.style.display = show ? 'block' : 'none';
        if (show) visibleEl = q;
        q.querySelectorAll('.taste-profile-answer').forEach(btn => {
          btn.classList.remove('selected');
        });
      });

      // In inline mode, scroll the question into view
      if (this.isInline && visibleEl && questionNumber > 0) {
        requestAnimationFrame(() => {
          const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-group-height')) || 120;
          const top = visibleEl.getBoundingClientRect().top + window.pageYOffset - headerH - 16;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        });
      }
    }

    updateProgress() {
      const progress = (this.currentQuestion / this.totalQuestions) * 100;
      if (this.progressFill) this.progressFill.style.width = progress + '%';
      if (this.progressText) this.progressText.textContent = this.currentQuestion;
      const progressEl = this.popup.querySelector('.taste-profile-progress');
      if (progressEl) {
        progressEl.style.display = this.currentQuestion === 0 ? 'none' : '';
      }
      if (this.backBtn) {
        this.backBtn.style.display = this.currentQuestion > 1 ? 'flex' : 'none';
      }
    }

    goBack() {
      if (this.currentQuestion > 1) {
        const lastAnswer = this.answers.pop();
        if (lastAnswer) {
          if (lastAnswer.color) {
            this.colorPreference = 'both';
          } else {
            this.targetX -= lastAnswer.x;
            this.targetY -= lastAnswer.y;
            this.targetZ -= lastAnswer.z || 0;
          }
        }
        this.currentQuestion--;
        this.updateProgress();
        this.showQuestion(this.currentQuestion);
        this.saveQuizState();
      }
    }

    saveQuizState() {
      const state = {
        currentQuestion: this.currentQuestion,
        targetX: this.targetX,
        targetY: this.targetY,
        targetZ: this.targetZ,
        colorPreference: this.colorPreference,
        answers: this.answers
      };
      sessionStorage.setItem(STORAGE_KEYS.QUIZ_STATE, JSON.stringify(state));
    }

    getMetaContent(name) {
      const meta = document.querySelector(`meta[name="${name}"]`);
      return meta ? String(meta.getAttribute('content') || '').trim() : '';
    }

    buildVinpopAuthHeaders() {
      const customerId = this.getMetaContent('customer-id');
      const timestamp = this.getMetaContent('friends-auth-timestamp');
      const signature = this.getMetaContent('friends-auth-signature');
      if (!customerId || !timestamp || !signature) {
        throw new Error('vinpop_auth_unavailable');
      }

      return {
        'Content-Type': 'application/json',
        'X-Customer-Id': customerId,
        'X-Timestamp': timestamp,
        'X-Signature': signature
      };
    }

    buildQuizSavePayload(quizzType) {
      const fieldByQuestion = {
        1: 'color',
        2: 'coffee',
        3: 'chocolate',
        4: 'salad',
        5: 'cheese',
        6: 'mood'
      };
      const payload = { quizz_type: quizzType };

      Object.keys(fieldByQuestion).forEach(question => {
        const answer = this.answers.find(item => Number(item.question) === Number(question));
        const value = answer ? Number(answer.choice) + 1 : NaN;
        if (value >= 1 && value <= 3) {
          payload[fieldByQuestion[question]] = value;
        }
      });

      return payload;
    }

    async saveQuizToApi() {
      const payload = this.buildQuizSavePayload('quizz-tasting');
      const requiredFields = ['color', 'coffee', 'chocolate', 'salad', 'cheese', 'mood'];
      if (!requiredFields.every(field => payload[field] >= 1 && payload[field] <= 3)) {
        throw new Error('quiz_answers_incomplete');
      }

      const apiBase = (this.getMetaContent('api-url') || 'https://api.dev.vinpop.fr:8443').replace(/\/+$/, '');
      const response = await fetch(`${apiBase}/api/quizz/save`, {
        method: 'POST',
        headers: this.buildVinpopAuthHeaders(),
        credentials: 'omit',
        mode: 'cors',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('quiz_save_failed_' + response.status);
      }

      return response;
    }

    ensureQuizSavedToApi() {
      const payload = this.buildQuizSavePayload('quizz-tasting');
      const signature = JSON.stringify(payload);
      if (this.quizSavePromise && this.lastQuizSaveSignature === signature) {
        return this.quizSavePromise;
      }

      this.lastQuizSaveSignature = signature;
      this.quizSavePromise = this.saveQuizToApi();
      return this.quizSavePromise;
    }

    restoreQuizState(state) {
      this.currentQuestion = state.currentQuestion;
      this.targetX = state.targetX;
      this.targetY = state.targetY;
      this.targetZ = state.targetZ;
      this.colorPreference = state.colorPreference;
      this.answers = state.answers;

      // Show quiz and correct question
      if (this.isInline) {
        this.popup.style.display = '';
      } else {
        this.popup.style.display = 'block';
        document.body.style.overflow = 'hidden';
      }

      if (this.revealEl) this.revealEl.style.display = 'none';
      if (this.loadingEl) this.loadingEl.style.display = 'none';

      this.updateProgress();
      this.showQuestion(this.currentQuestion);

      // Re-highlight previously selected answers
      this.answers.forEach(a => {
        const qEl = this.popup.querySelector(`.taste-profile-question[data-question="${a.question}"]`);
        if (qEl) {
          const btns = qEl.querySelectorAll('.taste-profile-answer');
          if (btns[a.choice]) btns[a.choice].classList.add('selected');
        }
      });

      console.log('🔄 Tasting quiz restored to question', this.currentQuestion);
    }

    showRevealScreen() {
      this.loadingEl.style.display = 'none';
      const progressEl = this.popup.querySelector('.taste-profile-progress');
      if (progressEl) progressEl.style.display = 'none';
      if (this.revealEl) {
        this.revealEl.style.display = 'block';
      } else {
        // Reveal screen removed from template — go directly to results
        this.revealResults();
      }
    }

    revealResults() {
      if (this.recommendedWines) {
        this.markQuizCompleted();
        this.displayResults(this.recommendedWines);
      }
    }

    markQuizCompleted() {
      localStorage.setItem(STORAGE_KEYS.QUIZ_COMPLETED, 'true');
      sessionStorage.setItem(STORAGE_KEYS.QUIZ_COMPLETED, 'true');
    }

    isQuizCompleted() {
      return localStorage.getItem(STORAGE_KEYS.QUIZ_COMPLETED) === 'true' ||
             sessionStorage.getItem(STORAGE_KEYS.QUIZ_COMPLETED) === 'true';
    }

    async fetchResults() {
      const requestSeq = ++this.fetchRequestSeq;

      // Show loading (skip in tweak-refresh mode)
      if (!this._tweakRefresh) {
        this.popup.querySelectorAll('.taste-profile-question').forEach(q => q.style.display = 'none');
        this.loadingEl.style.display = 'block';
      }

      try {
        await this.ensureQuizSavedToApi().catch(error => {
          console.warn('TastingQuiz: could not save quiz answers before results:', error);
        });

        const apiUrl = document.querySelector('meta[name="api-url"]')?.content || 'https://api.dev.vinpop.fr:8443';
        const response = await fetch(`${apiUrl}/api/wines/getMapAll`);
        if (!response.ok) throw new Error('Failed to fetch wine data');

        const wines = await response.json();

        // Safety: exclude wine IDs that are no longer in Shopify catalog
        const excludedIds = ['15510626763139'];
        const eligibleWines = wines.filter(w => !excludedIds.includes(String(w.wine_id)));

        // Filter by color preference
        let filteredWines = eligibleWines;
        if (this.colorPreference === 'red') {
          const redWines = eligibleWines.filter(w => (w.color || '').toUpperCase() === 'R');
          filteredWines = redWines.length >= 3 ? redWines : eligibleWines;
        } else if (this.colorPreference === 'white') {
          const whiteWines = eligibleWines.filter(w => (w.color || '').toUpperCase() === 'W');
          filteredWines = whiteWines.length >= 3 ? whiteWines : eligibleWines;
        }

        if (filteredWines.length === 0 && wines.length > 0) {
          filteredWines = wines;
        }

        console.log(`🧪 Color preference: ${this.colorPreference}, filtered: ${filteredWines.length}/${eligibleWines.length}`);

        // Filter to in-stock wines only
        const catalog = window.shopCatalog || {};
        const beforeStockCount = filteredWines.length;
        const inStockWines = filteredWines.filter(w => {
          const p = catalog[String(w.wine_id)];
          return p && p.available;
        });
        filteredWines = inStockWines.length >= 3 ? inStockWines : filteredWines;
        console.log(`📦 Stock filter: ${inStockWines.length}/${beforeStockCount} wines in stock`);

        // Calculate scores
        const winesWithScores = this.calculateScores(filteredWines);

        // Select top wines
        let topWines;
        if (this.colorPreference === 'both') {
          const sorted = winesWithScores.sort(compareMatchScore);
          const topRed = sorted.filter(w => (w.color || '').toUpperCase() === 'R').slice(0, 10);
          const topWhite = sorted.filter(w => (w.color || '').toUpperCase() === 'W').slice(0, 10);
          topWines = [...topRed, ...topWhite].sort(compareMatchScore);
        } else {
          topWines = winesWithScores.sort(compareMatchScore).slice(0, 10);
        }

        if (requestSeq !== this.fetchRequestSeq) {
          console.log('🧪 Skipping stale tasting refresh', requestSeq, 'latest is', this.fetchRequestSeq);
          return;
        }

        this.recommendedWines = topWines;
        this.showRevealScreen();

      } catch (error) {
        if (requestSeq !== this.fetchRequestSeq) return;
        console.error('❌ Quizz-Tasting: Error fetching results:', error);
        this.showError();
      }
    }

    calculateScores(wines) {
      // Normalize Z axis
      const rawZvals = wines.map(w => parseFloat(w.map_intensity) || 0);
      const zMin = Math.min.apply(null, rawZvals);
      const zMax = Math.max.apply(null, rawZvals);
      const zCenter = (zMin + zMax) / 2;
      const zHalf = (zMax - zMin) / 2 || 1;

      // Calibrate user XYZ into wine coordinate space
      const rawXvals = wines.map(w => parseFloat(w.map_x) || 0);
      const rawYvals = wines.map(w => parseFloat(w.map_y) || 0);
      const xMin = Math.min.apply(null, rawXvals), xMax = Math.max.apply(null, rawXvals);
      const yMin = Math.min.apply(null, rawYvals), yMax = Math.max.apply(null, rawYvals);
      const xCenter = (xMin + xMax) / 2, xHalf = (xMax - xMin) / 2 || 1;
      const yCenter = (yMin + yMax) / 2, yHalf = (yMax - yMin) / 2 || 1;
      const quizRange = 1.0;

      const calX = xCenter + (this.targetX / quizRange) * xHalf;
      const calY = yCenter + (this.targetY / quizRange) * yHalf;
      const calZ = this.targetZ / quizRange;

      // Store data for 3D taste map visualization
      sessionStorage.setItem('vinpop_user_xyz', JSON.stringify({ x: calX, y: calY, z: calZ }));
      const allWinesXYZ = wines.map(w => ({
        id: String(w.wine_id),
        x: parseFloat(w.map_x) || 0,
        y: parseFloat(w.map_y) || 0,
        z: ((parseFloat(w.map_intensity) || 0) - zCenter) / zHalf,
        color: (w.color || '').toUpperCase()
      }));
      sessionStorage.setItem('vinpop_all_wines_xyz', JSON.stringify(allWinesXYZ));

      // Calculate distances
      const withDistances = wines.map(wine => {
        const wineX = parseFloat(wine.map_x) || 0;
        const wineY = parseFloat(wine.map_y) || 0;
        const wineZ = ((parseFloat(wine.map_intensity) || 0) - zCenter) / zHalf;
        const distance = Math.sqrt(
          Math.pow(calX - wineX, 2) +
          Math.pow(calY - wineY, 2) +
          Math.pow(calZ - wineZ, 2)
        );
        return { ...wine, distance };
      });

      // Dynamic multiplier to guarantee top 16 at 80%+
      const GUARANTEE_COUNT = 16;
      const MIN_SCORE = 80;
      let multiplier = 15;
      const sorted = [...withDistances].sort((a, b) => a.distance - b.distance);
      if (sorted.length >= GUARANTEE_COUNT) {
        const distAtGuarantee = sorted[GUARANTEE_COUNT - 1].distance;
        if (distAtGuarantee > 0) {
          const maxMult = (100 - MIN_SCORE) / distAtGuarantee;
          if (multiplier > maxMult) multiplier = Math.max(1, maxMult);
        }
      } else if (sorted.length > 0) {
        const distLast = sorted[sorted.length - 1].distance;
        if (distLast > 0) {
          const maxMult = (100 - MIN_SCORE) / distLast;
          if (multiplier > maxMult) multiplier = Math.max(1, maxMult);
        }
      }

      return withDistances.map(wine => {
        let score = 100 - (wine.distance * multiplier);
        score = Math.max(0, Math.min(100, score));
        return { ...wine, score: Math.round(score) };
      });
    }

    displayResults(wines) {
      const isTweakRefresh = this._tweakRefresh;
      this._tweakRefresh = false;

      // Store results
      sessionStorage.setItem(STORAGE_KEYS.LAST_RESULTS, JSON.stringify(wines));
      localStorage.setItem(STORAGE_KEYS.LAST_RESULTS_PERSIST, JSON.stringify(wines));
      sessionStorage.setItem('vinpop_last_results', JSON.stringify(wines));
      if (!isTweakRefresh) {
        sessionStorage.setItem(STORAGE_KEYS.QUIZ_HINT_PENDING, 'true');
      }

      // Store taste profile
      const choices = {};
      this.answers.forEach(a => { choices['q' + a.question] = a.choice; });
      const profile = {
        colorPreference: this.colorPreference,
        targetX: this.targetX,
        targetY: this.targetY,
        targetZ: this.targetZ,
        choices: choices
      };
      sessionStorage.setItem(STORAGE_KEYS.TASTE_PROFILE, JSON.stringify(profile));
      sessionStorage.setItem(STORAGE_KEYS.QUIZ_ANSWERS, JSON.stringify(this.answers));

      this.markQuizCompleted();

      if (!isTweakRefresh) {
        if (this.isInline) {
          // Inline mode: show completed state with retake button (don't hide quiz)
          showCompletedState(this.popup);
        } else {
          this.closePopup();
        }
      }

      renderTastingQuizResults(wines);
    }

    showError() {
      this.loadingEl.style.display = 'none';
      const errorDiv = document.createElement('div');
      errorDiv.className = 'taste-profile-error';
      errorDiv.innerHTML = '<p>Sorry, something went wrong. Please try again.</p><button class="btn" onclick="location.reload()">Retry</button>';
      this.popup.querySelector('.taste-profile-questions').appendChild(errorDiv);
    }
  }

  // ========== TWEAK PANEL — TASTING QUIZ ==========

  function selectTastingTweakAnswer(quiz, qNum, choiceIndex, optionEl) {
    // Replace answer for this question, keep all others
    quiz.answers = quiz.answers.filter(a => a.question !== qNum);
    const colorVal = optionEl.dataset.color;
    if (colorVal) {
      quiz.answers.push({ question: qNum, choice: choiceIndex, color: colorVal });
    } else {
      quiz.answers.push({
        question: qNum,
        choice: choiceIndex,
        x: parseFloat(optionEl.dataset.x) || 0,
        y: parseFloat(optionEl.dataset.y) || 0,
        z: parseFloat(optionEl.dataset.z) || 0
      });
    }
    quiz.answers.sort((a, b) => a.question - b.question);

    // Recalculate taste coordinates from all answers
    quiz.targetX = 0; quiz.targetY = 0; quiz.targetZ = 0;
    quiz.colorPreference = 'both';
    quiz.answers.forEach(a => {
      if (a.color) {
        quiz.colorPreference = a.color;
      } else {
        quiz.targetX += (a.x || 0);
        quiz.targetY += (a.y || 0);
        quiz.targetZ += (a.z || 0);
      }
    });
    console.log('🔧 Tasting TWEAK: q' + qNum + '=' + choiceIndex + ' color=' + quiz.colorPreference + ' X=' + quiz.targetX.toFixed(3) + ' Y=' + quiz.targetY.toFixed(3) + ' Z=' + quiz.targetZ.toFixed(3));

    // Fade out results grid
    const resultsEl = document.getElementById('js-tasting-results');
    if (resultsEl) {
      resultsEl.style.transition = 'opacity 0.22s ease';
      resultsEl.style.opacity = '0';
    }
    const tweakPanel = document.getElementById('vinpop-tasting-tweak-panel');
    if (tweakPanel) {
      tweakPanel.style.transition = 'opacity 0.22s ease';
      tweakPanel.style.opacity = '0';
    }

    quiz._tweakRefresh = true;
    setTimeout(function() { quiz.fetchResults(); }, 240);
  }

  function renderTastingTweakPanel(quiz) {
    if (!quiz || !quiz.answers || quiz.answers.length === 0) return;

    // Remove previous panel (re-render on each update to reflect current selection)
    const oldPanel = document.getElementById('vinpop-tasting-tweak-panel');
    if (oldPanel) oldPanel.remove();

    const questionDefs = {
      1: [
        { text: 'Red Wine',   dataColor: 'red' },
        { text: 'White Wine', dataColor: 'white' },
        { text: 'Both',       dataColor: 'both' }
      ],
      2: [
        { text: 'Black',              x: 0,    y: 0.3,  z: 0.3 },
        { text: 'Cappuccino / Latte', x: 0,    y: -0.3, z: 0 },
        { text: 'Tea or Water',       x: -0.1, y: -0.1, z: -0.3 }
      ],
      3: [
        { text: 'Milk or White Chocolate', x: 0.4,  y: -0.2, z: -0.2 },
        { text: 'Dark Chocolate >70%',     x: -0.2, y: 0.3,  z: 0.3 },
        { text: 'Fruit Jellies / Gummies', x: -0.3, y: 0,    z: -0.1 }
      ],
      4: [
        { text: 'Lemon & Vinaigrette', x: -0.4, y: 0,    z: 0.1 },
        { text: 'Creamy / Mayonnaise', x: 0.2,  y: -0.2, z: 0.1 },
        { text: 'Olive Oil & Salt',    x: 0,    y: 0,    z: 0 }
      ],
      5: [
        { text: 'Brie or Mozzarella',          x: 0, y: -0.4, z: -0.4 },
        { text: 'Old Parmesan or Aged Gouda',  x: 0, y: 0.4,  z: 0.4 }
      ],
      6: [
        { text: 'Something Fresh & Fruity',  x: -0.3, y: 0,    z: -0.3 },
        { text: 'Something Smooth & Round',  x: 0.2,  y: -0.2, z: 0 },
        { text: 'Something Bold & Powerful', x: 0,    y: 0.3,  z: 0.4 }
      ]
    };

    const topicLabels = { 1: '🍷 Colour', 2: '☕ Coffee', 3: '🍫 Chocolate', 4: '🥗 Dressing', 5: '🧀 Cheese', 6: '🎯 Mood' };

    const rows = [];
    for (var qNum = 1; qNum <= 6; qNum++) {
      var opts = questionDefs[qNum];
      if (!opts) continue;
      var ans = quiz.answers.find(function(a) { return a.question === qNum; });
      var currentOpt = (ans && ans.choice != null) ? opts[ans.choice] : null;
      var currentText = currentOpt ? currentOpt.text : '—';

      var optionsHTML = opts.map(function(opt, i) {
        var isSelected = ans && i === ans.choice;
        var dataAttrs = opt.dataColor
          ? 'data-color="' + opt.dataColor + '"'
          : 'data-x="' + opt.x + '" data-y="' + opt.y + '" data-z="' + opt.z + '"';
        return '<button class="wr-tweak-option' + (isSelected ? ' wr-tweak-option--selected' : '') + '" data-q="' + qNum + '" data-choice="' + i + '" ' + dataAttrs + ' type="button">' + opt.text + '</button>';
      }).join('');

      rows.push(
        '<details class="wr-tweak-question">' +
          '<summary class="wr-tweak-summary">' +
            '<span class="wr-tweak-topic">' + (topicLabels[qNum] || 'Q' + qNum) + '</span>' +
            '<span class="wr-tweak-current">' + currentText + '</span>' +
            '<span class="wr-tweak-chevron" aria-hidden="true">›</span>' +
          '</summary>' +
          '<div class="wr-tweak-options">' + optionsHTML + '</div>' +
        '</details>'
      );
    }

    if (rows.length === 0) return;

    var panel = document.createElement('div');
    panel.id = 'vinpop-tasting-tweak-panel';
    panel.innerHTML =
      '<details class="wr-bundle__tweak-panel">' +
        '<summary class="wr-tweak-panel__toggle">Think we always offer the same wines? Change an answer and watch your selection mutate in real-time.<span class="wr-tweak-panel__arrow" aria-hidden="true">›</span></summary>' +
        '<div class="wr-tweak-panel__body"><div class="wr-tweak-questions">' + rows.join('') + '</div></div>' +
      '</details>';

    // Insert the panel before the results grid
    var resultsEl = document.getElementById('js-tasting-results');
    if (resultsEl && resultsEl.parentNode) {
      resultsEl.parentNode.insertBefore(panel, resultsEl);
    } else {
      return;
    }

    // Restore opacity after insertion
    panel.style.opacity = '1';
    if (resultsEl) resultsEl.style.opacity = '1';

    // One-at-a-time accordion for questions
    var details = panel.querySelectorAll('.wr-tweak-question');
    details.forEach(function(det) {
      det.addEventListener('toggle', function() {
        if (det.open) details.forEach(function(other) { if (other !== det) other.removeAttribute('open'); });
      });
    });

    // Wire click handlers on option buttons
    panel.querySelectorAll('.wr-tweak-option').forEach(function(btn) {
      btn.addEventListener('click', function() {
        selectTastingTweakAnswer(quiz, parseInt(btn.dataset.q), parseInt(btn.dataset.choice), btn);
      });
    });

    console.log('🔧 Tasting tweak panel rendered');
  }

  function renderTastingAxisSliders(quiz) {
    if (!quiz) return;

    var lang = (document.documentElement.lang || 'en').toLowerCase();
    var isNL = lang.indexOf('nl') === 0;

    if (!document.getElementById('vinpop-axis-slider-style')) {
      var sliderStyle = document.createElement('style');
      sliderStyle.id = 'vinpop-axis-slider-style';
      sliderStyle.textContent = '' +
        '.vinpop-axis-range{' +
          '-webkit-appearance:none;' +
          'appearance:none;' +
          'height:6px;' +
          'border-radius:999px;' +
          'outline:none;' +
        '}' +
        '.vinpop-axis-range::-webkit-slider-runnable-track{' +
          'height:6px;' +
          'border-radius:999px;' +
          'background:transparent;' +
        '}' +
        '.vinpop-axis-range::-webkit-slider-thumb{' +
          '-webkit-appearance:none;' +
          'appearance:none;' +
          'width:16px;' +
          'height:16px;' +
          'border-radius:50%;' +
          'background:#E07A5F;' +
          'border:2px solid #fff;' +
          'box-shadow:0 2px 8px rgba(0,0,0,0.12);' +
          'margin-top:-5px;' +
          'cursor:pointer;' +
        '}' +
        '.vinpop-axis-range::-moz-range-track{' +
          'height:6px;' +
          'border-radius:999px;' +
          'background:rgba(129,178,154,0.18);' +
        '}' +
        '.vinpop-axis-range::-moz-range-progress{' +
          'height:6px;' +
          'border-radius:999px;' +
          'background:#81B29A;' +
        '}' +
        '.vinpop-axis-range::-moz-range-thumb{' +
          'width:16px;' +
          'height:16px;' +
          'border-radius:50%;' +
          'background:#E07A5F;' +
          'border:2px solid #fff;' +
          'box-shadow:0 2px 8px rgba(0,0,0,0.12);' +
          'cursor:pointer;' +
        '}';
      document.head.appendChild(sliderStyle);
    }

    var oldSliders = document.getElementById('vinpop-tasting-axis-sliders');
    if (oldSliders) oldSliders.remove();

    var instructionEl = document.getElementById('js-tasting-choice-instruction');
    var resultsEl = document.getElementById('js-tasting-results');
    if (!resultsEl || !resultsEl.parentNode) return;

    var sliders = document.createElement('div');
    sliders.id = 'vinpop-tasting-axis-sliders';
    sliders.style.cssText = 'max-width:900px;margin:0 auto 16px auto;padding:0 8px;';

    var xVal = Number(quiz.targetX || 0);
    var yVal = Number(quiz.targetY || 0);
    var zVal = Number(quiz.targetZ || 0);

    function clampAxis(v) {
      return Math.max(-1, Math.min(1, Number(v || 0)));
    }

    xVal = clampAxis(xVal);
    yVal = clampAxis(yVal);
    zVal = clampAxis(zVal);

    function axisCard(cfg) {
      return (
        '<div style="background:rgba(255,255,255,0.84);border-radius:14px;padding:10px 12px 9px 12px;box-shadow:0 8px 18px rgba(0,0,0,0.08);backdrop-filter:blur(6px);">' +
          '<div style="text-align:center;">' +
            '<div>' +
              '<div style="font-family:Inter,sans-serif;font-weight:700;font-size:15px;line-height:1.15;color:#111;">' + cfg.title + '</div>' +
              (cfg.subtitle ? '<div style="margin-top:3px;font-family:Inter,sans-serif;font-weight:500;font-size:11px;line-height:1.25;color:rgba(17,17,17,0.72);">' + cfg.subtitle + '</div>' : '') +
            '</div>' +
            '' +
          '</div>' +
          '<div style="margin-top:7px;background:transparent;border-radius:10px;padding:6px 2px 5px 2px;">' +
            '<input class="vinpop-axis-range" id="' + cfg.inputId + '" type="range" min="-1" max="1" step="0.01" value="' + cfg.value.toFixed(2) + '" style="width:100%;background:rgba(129,178,154,0.18);">' +
          '</div>' +
          '<div style="display:flex;justify-content:space-between;gap:10px;margin-top:6px;font-family:Inter,sans-serif;font-weight:600;font-size:10px;line-height:1.2;color:rgba(17,17,17,0.72);text-transform:uppercase;letter-spacing:0.35px;">' +
            '<span style="text-align:left;max-width:34%;">' + cfg.min + '</span>' +
            '<span style="text-align:center;max-width:32%;">' + cfg.mid + '</span>' +
            '<span style="text-align:right;max-width:34%;">' + cfg.max + '</span>' +
          '</div>' +
        '</div>'
      );
    }

    sliders.innerHTML =
      '<div style="background:radial-gradient(140% 120% at 0% 0%,#fff 0%,#f8f4ef 52%,#f2ede6 100%);border-radius:18px;padding:12px;box-shadow:0 12px 28px rgba(15,23,42,0.10);">' +
        '<div style="font-family:Inter,sans-serif;font-weight:700;font-size:18px;line-height:1.2;color:#111;margin-bottom:8px;">' + (isNL ? 'Maak je wijnmatch direct beter' : 'Move 3 sliders and instantly see which 2 wines fit you best.') + '</div>' +
        '<div style="display:grid;gap:8px;">' +
          axisCard({
            title: isNL ? 'Fris of rijk?' : 'Fresh or rich?',
            subtitle: '',
            value: xVal,
            valueId: 'vinpop-axis-x-val',
            inputId: 'vinpop-axis-x',
            min: isNL ? 'Zoals citroen' : 'Like lemon',
            mid: isNL ? 'Zoals perzik' : 'Like peach',
            max: isNL ? 'Zoals honing' : 'Like honey',
            minBadge: isNL ? 'Fris' : 'Fresh',
            midBadge: isNL ? 'Balans' : 'Balanced',
            maxBadge: isNL ? 'Rijk' : 'Rich'
          }) +
          axisCard({
            title: isNL ? 'Zacht of sterk?' : 'Soft or strong?',
            subtitle: '',
            value: yVal,
            valueId: 'vinpop-axis-y-val',
            inputId: 'vinpop-axis-y',
            min: isNL ? 'Zoals witte chocolade' : 'Like white chocolate',
            mid: isNL ? 'Zoals melkchocolade' : 'Like milk chocolate',
            max: isNL ? 'Zoals pure chocolade' : 'Like dark chocolate',
            minBadge: isNL ? 'Zacht' : 'Soft',
            midBadge: isNL ? 'Soepel' : 'Smooth',
            maxBadge: isNL ? 'Krachtig' : 'Bold'
          }) +
          axisCard({
            title: isNL ? 'Licht of intens?' : 'Light or intense?',
            subtitle: '',
            value: zVal,
            valueId: 'vinpop-axis-z-val',
            inputId: 'vinpop-axis-z',
            min: isNL ? 'Zoals thee' : 'Like tea',
            mid: isNL ? 'Zoals cappuccino' : 'Like cappuccino',
            max: isNL ? 'Zoals espresso' : 'Like espresso',
            minBadge: isNL ? 'Licht' : 'Light',
            midBadge: isNL ? 'Medium' : 'Medium',
            maxBadge: isNL ? 'Intens' : 'Intense'
          }) +
        '</div>' +
      '</div>';

    if (instructionEl && instructionEl.parentNode) {
      if (instructionEl.nextSibling) {
        instructionEl.parentNode.insertBefore(sliders, instructionEl.nextSibling);
      } else {
        instructionEl.parentNode.appendChild(sliders);
      }
    } else {
      resultsEl.parentNode.insertBefore(sliders, resultsEl);
    }

    var xInput = document.getElementById('vinpop-axis-x');
    var yInput = document.getElementById('vinpop-axis-y');
    var zInput = document.getElementById('vinpop-axis-z');

    function paintSlider(input) {
      var value = clampAxis(input.value);
      var percent = ((value + 1) / 2) * 100;
      input.style.background = 'linear-gradient(90deg, #81B29A 0%, #81B29A ' + percent + '%, rgba(129,178,154,0.18) ' + percent + '%, rgba(129,178,154,0.18) 100%)';
    }

    paintSlider(xInput);
    paintSlider(yInput);
    paintSlider(zInput);

    var refreshTimer = null;
    function refreshFromSliders() {
      var nx = clampAxis(xInput.value);
      var ny = clampAxis(yInput.value);
      var nz = clampAxis(zInput.value);

      paintSlider(xInput);
      paintSlider(yInput);
      paintSlider(zInput);

      quiz.targetX = nx;
      quiz.targetY = ny;
      quiz.targetZ = nz;

      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(function() {
        quiz._tweakRefresh = true;
        quiz.fetchResults();
      }, 120);
    }

    xInput.addEventListener('input', refreshFromSliders);
    yInput.addEventListener('input', refreshFromSliders);
    zInput.addEventListener('input', refreshFromSliders);
  }

  function compareMatchScore(a, b) {
    var scoreDelta = (Number(b.score) || 0) - (Number(a.score) || 0);
    if (scoreDelta !== 0) return scoreDelta;

    var aDistance = Number.isFinite(Number(a.distance)) ? Number(a.distance) : Number.POSITIVE_INFINITY;
    var bDistance = Number.isFinite(Number(b.distance)) ? Number(b.distance) : Number.POSITIVE_INFINITY;
    if (aDistance !== bDistance) return aDistance - bDistance;

    return String(a.id || '').localeCompare(String(b.id || ''));
  }

  // ========== RENDER TASTING QUIZ RESULTS ==========
  function renderTastingQuizResults(apiWines) {
    console.log('🧪 Rendering tasting quiz results:', apiWines.length, 'API wines to map');

    const catalog = window.quizzTastingCatalog;
    const byWineId = window.quizzTastingByWineId;

    if (!catalog) {
      console.error('❌ quizzTastingCatalog not loaded');
      return;
    }

    // Build score map from API: wine_id → { score, distance, color }
    const apiScoreMap = {};
    apiWines.forEach(apiWine => {
      apiScoreMap[String(apiWine.wine_id)] = {
        score: apiWine.score,
        distance: apiWine.distance || 0,
        color: apiWine.color || ''
      };
    });

    // Map ALL quizz-tasting products, giving each a score via their productidmatch/productid metafield
    const allProducts = [];
    const catalogProducts = Object.values(catalog);
    let matchedCount = 0;

    catalogProducts.forEach(product => {
      const wineId = (product.productidmatch || product.productid) ? String(product.productidmatch || product.productid) : null;
      const apiData = wineId ? apiScoreMap[wineId] : null;

      if (apiData) {
        matchedCount++;
        allProducts.push({
          ...product,
          score: apiData.score,
          distance: apiData.distance,
          wineColor: apiData.color
        });
      } else {
        // No API match — include with low default score so it still appears at the end
        allProducts.push({
          ...product,
          score: 70,
          distance: 999,
          wineColor: ''
        });
      }
    });

    console.log(`🧪 Mapped ${matchedCount}/${catalogProducts.length} products to API scores`);

    allProducts.sort(compareMatchScore);

    // Color balance for 'both' preference (only among matched products)
    const profile = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.TASTE_PROFILE) || '{}');
    const colorPref = profile.colorPreference || 'both';

    let finalProducts;
    if (colorPref === 'both' && matchedCount >= 6) {
      const matched = allProducts.filter(p => p.distance < 999);
      const unmatched = allProducts.filter(p => p.distance >= 999);
      const reds = matched.filter(p => (p.wineColor || '').toUpperCase() === 'R').slice(0, 8);
      const whites = matched.filter(p => (p.wineColor || '').toUpperCase() === 'W').slice(0, 8);
      const others = matched.filter(p => !['R', 'W'].includes((p.wineColor || '').toUpperCase()));
      const balanced = [...reds, ...whites, ...others.slice(0, Math.max(0, 16 - reds.length - whites.length))]
        .sort(compareMatchScore);
      finalProducts = [...balanced, ...unmatched];
      console.log(`🧪 Balanced: ${reds.length} red + ${whites.length} white + ${unmatched.length} unmatched`);
    } else {
      finalProducts = allProducts;
    }

    console.log(`🧪 Final display: ${finalProducts.length} tasting products (showing top 2)`);

    // Limit to 2 results only
    finalProducts = finalProducts.slice(0, 2);

    // Store for re-apply after Filterable clobber
    window.__tastingQuizProducts = finalProducts;

    waitForGridAndRender(finalProducts);
  }

  // ========== GRID RENDERING ==========
  // Same strategy as wine-recommendation-manager.js on /collections/quizz:
  // Remove native grid entirely, create new container, only add matched cards.

  function waitForGridAndRender(products, attempt) {
    attempt = attempt || 0;
    var maxAttempts = 20;

    // Unhide the product-grid section (hidden by inline quiz CSS with !important)
    var gridSections = document.querySelectorAll('[id^="shopify-section-template"][id$="__product-grid"]');
    gridSections.forEach(function(sec) {
      sec.style.setProperty('display', 'block', 'important');
    });

    var grid = document.querySelector('ul.grid.gap-theme') ||
               document.querySelector('#facets-results ul.grid') ||
               document.querySelector('#facets-results ul') ||
               document.querySelector('#MainContent ul.grid');

    if (!grid && attempt < maxAttempts) {
      setTimeout(function() { waitForGridAndRender(products, attempt + 1); }, 300);
      return;
    }

    if (!grid) {
      console.error('❌ Could not find product grid after', maxAttempts, 'attempts');
      return;
    }

    replaceGridWithResults(products, grid);
  }

  function replaceGridWithResults(products, nativeGrid) {
    // Build lookup of matched product IDs
    var matchedById = {};
    products.forEach(function(product) {
      matchedById[String(product.id)] = product;
    });

    // Save original native cards on first render (before grid is replaced)
    var isRerender = nativeGrid.id === 'js-tasting-results';
    if (!isRerender && !window.__tastingOriginalCards) {
      var nativeCards = Array.from(nativeGrid.querySelectorAll('li[data-product-id]'));
      window.__tastingOriginalCards = nativeCards.map(function(c) { return c.cloneNode(true); });
      window.__tastingGridClassName = nativeGrid.className;
      console.log('💾 Saved', window.__tastingOriginalCards.length, 'original cards for re-render');
    }

    // Use saved cards on re-render, native cards on first render
    var cardSource = isRerender && window.__tastingOriginalCards
      ? window.__tastingOriginalCards
      : Array.from(nativeGrid.querySelectorAll('li[data-product-id]'));

    var matchedCards = [];
    cardSource.forEach(function(card) {
      var clone = card.cloneNode(true);
      var cardProductId = clone.getAttribute('data-product-id');
      var tileEl = clone.querySelector('.product-tile[data-product-id]');
      var tileProductId = tileEl ? tileEl.getAttribute('data-product-id') : null;
      var product = matchedById[cardProductId] || matchedById[tileProductId];

      if (product) {
        matchedCards.push({ card: clone, product: product });
      }
    });

    // Sort by score descending, then by closest distance to avoid sticky ties.
    matchedCards.sort(function(a, b) { return compareMatchScore(a.product, b.product); });

    // Create new container with same classes as native grid (same approach as /quizz)
    var gridParent = nativeGrid.parentNode;
    var gridClassName = isRerender ? (window.__tastingGridClassName || nativeGrid.className) : nativeGrid.className;

    var customContainer = document.createElement('ul');
    customContainer.id = 'js-tasting-results';
    customContainer.className = gridClassName;
    customContainer.style.width = '100%';
    customContainer.style.display = 'grid';
    customContainer.style.justifyContent = 'center';

    // Insert new container, then remove native grid
    gridParent.insertBefore(customContainer, nativeGrid);
    nativeGrid.remove();
    console.log('✅ Native grid replaced with custom container');

    // Add only matched cards (no % match badge)
    matchedCards.forEach(function(item) {
      addTastingCartButton(item.card);
      customContainer.appendChild(item.card);
    });

    // Remove native filters (same as /quizz)
    var filterSelectors = [
      '#native-filters-container', '#facets', '.facets', '[data-facets]',
      '.collection-filters', '.facets__sort', '[data-sort-by]',
      '.collection-sort', '#facets-results-count'
    ];
    filterSelectors.forEach(function(sel) {
      var el = document.querySelector(sel);
      if (el) el.remove();
    });

    // Hide prices
    document.querySelectorAll('[data-product-price]').forEach(function(el) {
      el.style.setProperty('display', 'none', 'important');
    });

    // Block Filterable popstate re-renders (same approach as /quizz)
    if (!window.__tastingPopstateGuard) {
      window.__tastingPopstateGuard = true;
      window.addEventListener('popstate', function(e) {
        var hasOurContainer = document.getElementById('js-tasting-results');
        if (hasOurContainer && hasOurContainer.children.length > 0) {
          e.stopImmediatePropagation();
          console.log('🛡️ Blocked Filterable popstate re-render');
        }
      }, true);
    }

    window.__tastingResultsRendered = true;
    console.log('✅ Quizz-tasting results rendered:', matchedCards.length, 'matched products');
    document.dispatchEvent(new CustomEvent('vinpop:results-rendered'));

    // Render controls
    if (useV2CollectionTheme) {
      var existingTweak = document.getElementById('vinpop-tasting-tweak-panel');
      if (existingTweak) existingTweak.remove();
    } else {
      renderTastingTweakPanel(window.tastingQuiz);
    }

    // Once results are shown, remove the original quiz question block from the page.
    // This keeps the focus on the two bottles + tweak panel.
    var quizQuestionsEl = document.querySelector('#taste-profile-popup .taste-profile-questions') ||
                          document.querySelector('.taste-profile-popup--inline .taste-profile-questions') ||
                          document.querySelector('.taste-profile-questions');
    if (quizQuestionsEl) {
      quizQuestionsEl.remove();
    }

    // Remove any remaining quiz chrome (progress/header container) once results are visible
    var popupEl = document.getElementById('taste-profile-popup');
    if (popupEl) {
      var progressEl = popupEl.querySelector('.taste-profile-progress');
      if (progressEl) progressEl.remove();

      if (popupEl.classList.contains('taste-profile-popup--inline')) {
        popupEl.remove();
      } else {
        popupEl.style.display = 'none';
      }
    }

    // Place instruction below tweak panel and above result cards
    var oldInstruction = document.getElementById('js-tasting-choice-instruction');
    if (oldInstruction) oldInstruction.remove();

    var isNL = (document.documentElement.lang || 'nl').toLowerCase().startsWith('nl');
    var instruction = document.createElement('div');
    instruction.id = 'js-tasting-choice-instruction';
    instruction.className = 'vinpop-tasting-choice-instruction';
    instruction.textContent = isNL
      ? 'Proef ze allebei en klik op degene die je verkiest.'
      : 'Taste both wines, then choose the one you like most.';
    instruction.style.cssText = 'margin:18px 0 16px 0;padding:0;border:0;background:transparent;color:#000;font-family:Inter,sans-serif;font-weight:700;font-size:clamp(24px,3.2vw,42px);line-height:1.06;letter-spacing:-0.02em;text-align:center;';

    var tweakPanelEl = document.getElementById('vinpop-tasting-tweak-panel');
    if (tweakPanelEl && tweakPanelEl.parentNode) {
      if (tweakPanelEl.nextSibling) {
        tweakPanelEl.parentNode.insertBefore(instruction, tweakPanelEl.nextSibling);
      } else {
        tweakPanelEl.parentNode.appendChild(instruction);
      }
    } else if (customContainer && customContainer.parentNode) {
      customContainer.parentNode.insertBefore(instruction, customContainer);
    }

    if (useV2CollectionTheme) {
      renderTastingAxisSliders(window.tastingQuiz);
    }

    // Show helper bubble only right after quiz completion render
    const shouldShowHint = sessionStorage.getItem(STORAGE_KEYS.QUIZ_HINT_PENDING) === 'true';
    if (shouldShowHint && matchedCards.length >= 2) {
      showPostRevealHint();
      sessionStorage.removeItem(STORAGE_KEYS.QUIZ_HINT_PENDING);
    }

    // Keep viewport stable: do not auto-scroll on results/tweak refresh.
  }

  function showPostRevealHint() {
    if (document.getElementById('vinpop-post-reveal-hint')) return;

    const isNL = (document.documentElement.lang || 'nl').toLowerCase().startsWith('nl');
    const title = isNL ? 'Let goed op' : 'Pay attention';
    const stepOneLabel = isNL ? 'Stap 1' : 'Step 1';
    const stepOne = isNL
      ? 'Proef eerst allebei de wijnen.'
      : 'Taste both wines before choosing.';
    const stepTwoLabel = isNL ? 'Stap 2' : 'Step 2';
    const stepTwo = isNL
      ? 'Klik op de wijn die je het lekkerst vindt.'
      : 'Click on the wine you like most.';
    const cta = isNL ? 'Laten we de wijnen proeven' : 'Let\'s taste the wines';

    const hint = document.createElement('div');
    hint.id = 'vinpop-post-reveal-hint';
    hint.style.cssText = 'position:fixed;inset:0;z-index:10040;background:rgba(17,12,8,.62);backdrop-filter:blur(10px);display:flex;align-items:center;justify-content:center;padding:24px;';
    hint.innerHTML =
      '<div role="dialog" aria-modal="true" style="position:relative;max-width:600px;width:100%;background:linear-gradient(145deg,#fffaf5 0%,#ffffff 58%,#edf5f0 100%);border:1px solid rgba(84,55,33,.12);border-radius:28px;box-shadow:0 30px 90px rgba(0,0,0,.32);padding:30px;color:#1f1f1f;overflow:hidden;">' +
        '<div style="position:absolute;inset:-70px auto auto -70px;width:240px;height:240px;background:radial-gradient(circle,rgba(129,178,154,.30) 0%,rgba(129,178,154,0) 70%);pointer-events:none;"></div>' +
        '<div style="position:absolute;right:-80px;bottom:-90px;width:260px;height:260px;background:radial-gradient(circle,rgba(224,122,95,.18) 0%,rgba(224,122,95,0) 72%);pointer-events:none;"></div>' +
        '<button type="button" aria-label="Close" style="position:absolute;top:18px;right:18px;border:0;background:rgba(255,255,255,.86);width:40px;height:40px;border-radius:999px;font-size:22px;line-height:1;cursor:pointer;color:#6d5b50;box-shadow:0 10px 24px rgba(0,0,0,.08);">×</button>' +
        '<div style="position:relative;">' +
          '<div style="font-family:inherit;font-weight:800;font-size:clamp(30px,4vw,44px);line-height:1.04;color:#1d140e;margin-bottom:20px;padding-right:44px;">' + title + '</div>' +
          '<div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;margin-bottom:22px;">' +
            '<div style="padding:20px;border-radius:22px;background:rgba(255,255,255,.84);border:1px solid rgba(31,20,14,.08);box-shadow:0 14px 34px rgba(63,37,18,.08);">' +
              '<div style="display:inline-flex;margin-bottom:10px;padding:7px 11px;border-radius:999px;background:#81B29A;color:#fff;font-size:12px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;">' + stepOneLabel + '</div>' +
              '<div style="font-size:18px;line-height:1.35;color:#2c211b;font-weight:750;">' + stepOne + '</div>' +
            '</div>' +
            '<div style="padding:20px;border-radius:22px;background:rgba(255,255,255,.84);border:1px solid rgba(31,20,14,.08);box-shadow:0 14px 34px rgba(63,37,18,.08);">' +
              '<div style="display:inline-flex;margin-bottom:10px;padding:7px 11px;border-radius:999px;background:#81B29A;color:#fff;font-size:12px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;">' + stepTwoLabel + '</div>' +
              '<div style="font-size:18px;line-height:1.35;color:#2c211b;font-weight:750;">' + stepTwo + '</div>' +
            '</div>' +
          '</div>' +
          '<button type="button" data-vinpop-hint-start style="width:100%;border:0;border-radius:999px;background:#81B29A;color:#fff;padding:15px 20px;font-size:16px;font-weight:800;cursor:pointer;box-shadow:0 14px 32px rgba(129,178,154,.30);">' + cta + '</button>' +
        '</div>' +
      '</div>';

    const closeBtn = hint.querySelector('button');
    if (closeBtn) {
      closeBtn.addEventListener('click', function() {
        hint.remove();
      });
    }
    const startBtn = hint.querySelector('[data-vinpop-hint-start]');
    if (startBtn) {
      startBtn.addEventListener('click', function() {
        hint.remove();
      });
    }

    hint.addEventListener('click', function(e) {
      if (e.target === hint) {
        hint.remove();
      }
    });

    document.body.appendChild(hint);
  }

  // ========== REAL WINE CTA (link to real product page) ==========
  // Resolve the variant ID of the real wine for a quizz-tasting proxy product.
  // Mirrors the logic from vinpop-favourites.js resolveLoveTargetProductIds()
  // but returns variant IDs instead of product IDs.
  function resolveRealWineVariant(cardProductId, callback) {
    var id = String(cardProductId || '');
    if (!id) { callback(null); return; }

    var catalogEntry = (window.quizzTastingCatalog || {})[id];
    if (!catalogEntry) { callback(null); return; }

    // Use productidmatch/productid (API wine ID) as the matchKey for quizz-tasting
    var matchKey = String(catalogEntry.productidmatch || catalogEntry.productid || '');
    if (!matchKey) { callback(null); return; }

    console.log('[resolveRealWineVariant]', { cardProductId: id, productidmatch: matchKey });

    var shopCatalog = window.shopCatalog || {};
    var shopCatalogIndex = window.shopCatalogIndex || {};
    var candidates = [];

    // 1) Search shopCatalog by relatedProductId === matchKey (prefer price > 0)
    for (var k1 in shopCatalog) {
      var entry1 = shopCatalog[k1] || {};
      if (String(entry1.relatedProductId || '') !== matchKey) continue;
      if (String(k1) === id) continue; // skip the proxy itself

      // Prefer products with price > 0
      if (!candidates.length && Number(entry1.price || 0) > 0) {
        var vars1 = entry1.variants || [];
        var av1 = vars1.filter(function(v) { return v.available; })[0] || vars1[0];
        if (av1) {
          console.log('[resolveRealWineVariant] Found in shopCatalog (price > 0):', k1, 'variant:', av1.id);
          callback(String(av1.id));
          return;
        }
      }
      candidates.push(k1);
    }

    // 1b) If no priced product found, try first candidate
    if (candidates.length && !candidates.some(function(k) { return shopCatalog[k] && shopCatalog[k].price > 0; })) {
      var firstCandidate = shopCatalog[candidates[0]];
      if (firstCandidate && firstCandidate.variants && firstCandidate.variants.length) {
        var avCand = firstCandidate.variants.filter(function(v) { return v.available; })[0] || firstCandidate.variants[0];
        if (avCand) {
          console.log('[resolveRealWineVariant] Found in shopCatalog (any):', candidates[0], 'variant:', avCand.id);
          callback(String(avCand.id));
          return;
        }
      }
    }

    // 2) Search shopCatalogIndex by r (relatedProductId) === matchKey, then fetch handle
    for (var k2 in shopCatalogIndex) {
      if (k2 === '_') continue;
      var entry2 = shopCatalogIndex[k2] || {};
      if (String(entry2.r || '') !== matchKey) continue;
      if (String(k2) === id) continue;

      var handle2 = entry2.h;
      if (handle2) {
        console.log('[resolveRealWineVariant] Found in shopCatalogIndex:', k2, 'handle:', handle2, 'fetching...');
        fetch('/products/' + handle2 + '.js')
          .then(function(r) { return r.json(); })
          .then(function(p) {
            var av2 = (p.variants || []).filter(function(v) { return v.available; })[0] || (p.variants || [])[0];
            if (av2) {
              console.log('[resolveRealWineVariant] Fetched variant:', av2.id);
              callback(String(av2.id));
            } else {
              console.log('[resolveRealWineVariant] No variants in fetched product');
              callback(null);
            }
          })
          .catch(function(err) {
            console.error('[resolveRealWineVariant] Fetch error:', err);
            callback(null);
          });
        return;
      }
    }

    // 3) Fallback: if matchKey itself is a Shopify product ID in the catalog, use it
    if (shopCatalog[matchKey] && shopCatalog[matchKey].variants) {
      var avFb = shopCatalog[matchKey].variants.filter(function(v) { return v.available; })[0] || shopCatalog[matchKey].variants[0];
      if (avFb) {
        console.log('[resolveRealWineVariant] Using matchKey as product ID:', matchKey, 'variant:', avFb.id);
        callback(String(avFb.id));
        return;
      }
    }

    if (shopCatalogIndex[matchKey] && shopCatalogIndex[matchKey].h) {
      var handleFb = shopCatalogIndex[matchKey].h;
      console.log('[resolveRealWineVariant] matchKey in index, fetching handle:', handleFb);
      fetch('/products/' + handleFb + '.js')
        .then(function(r) { return r.json(); })
        .then(function(p) {
          var avFb = (p.variants || []).filter(function(v) { return v.available; })[0] || (p.variants || [])[0];
          if (avFb) {
            console.log('[resolveRealWineVariant] Fetched from matchKey handle:', avFb.id);
            callback(String(avFb.id));
          } else {
            console.log('[resolveRealWineVariant] No variants in matchKey product');
            callback(null);
          }
        })
        .catch(function(err) {
          console.error('[resolveRealWineVariant] Fallback fetch error:', err);
          callback(null);
        });
      return;
    }

    console.log('[resolveRealWineVariant] No resolution path found');
    callback(null);
  }

  function resolveRealWineHandle(cardProductId, callback) {
    var id = String(cardProductId || '');
    if (!id) { callback(null); return; }

    var catalogEntry = (window.quizzTastingCatalog || {})[id];
    if (!catalogEntry) { callback(null); return; }

    // Use productidmatch/productid (API wine ID) as the matchKey for quizz-tasting
    var matchKey = String(catalogEntry.productidmatch || catalogEntry.productid || '');
    if (!matchKey) { callback(null); return; }

    var shopCatalog = window.shopCatalog || {};
    var shopCatalogIndex = window.shopCatalogIndex || {};
    var fallbackHandle = null;

    // 1) Search shopCatalog by relatedProductId === matchKey (prefer price > 0)
    for (var k1 in shopCatalog) {
      var entry1 = shopCatalog[k1] || {};
      if (String(entry1.relatedProductId || '') !== matchKey) continue;
      if (String(k1) === id) continue;
      if (!entry1.handle) continue;

      if (Number(entry1.price || 0) > 0) {
        callback(String(entry1.handle));
        return;
      }

      if (!fallbackHandle) fallbackHandle = String(entry1.handle);
    }

    // 2) Search shopCatalogIndex by r (relatedProductId) === matchKey
    for (var k2 in shopCatalogIndex) {
      if (k2 === '_') continue;
      var entry2 = shopCatalogIndex[k2] || {};
      if (String(entry2.r || '') !== matchKey) continue;
      if (String(k2) === id) continue;
      if (entry2.h) {
        callback(String(entry2.h));
        return;
      }
    }

    // 3) Fallback: if matchKey itself is a Shopify product ID in catalog/index
    if (shopCatalog[matchKey] && shopCatalog[matchKey].handle) {
      callback(String(shopCatalog[matchKey].handle));
      return;
    }

    if (shopCatalogIndex[matchKey] && shopCatalogIndex[matchKey].h) {
      callback(String(shopCatalogIndex[matchKey].h));
      return;
    }

    callback(fallbackHandle);
  }

  function addTastingCartButton(card) {
    var quickBuy = card.querySelector('.product-tile__quick-buy');
    if (!quickBuy) return;

    var productTile = card.querySelector('.product-tile[data-product-id]') || card;
    var cardProductId = productTile.getAttribute('data-product-id');
    if (!cardProductId) return;

    // Only add button if this card has catalog data (is a quizz-tasting proxy product)
    var catalogEntry = (window.quizzTastingCatalog || {})[String(cardProductId)];
    if (!catalogEntry) return;

    // Idempotent
    var existing = quickBuy.querySelector('.vinpop-tasting-cart-wrapper, .vinpop-tasting-see-wrapper');
    if (existing) existing.remove();

    var wrapper = document.createElement('div');
    wrapper.className = 'vinpop-fav-btn-wrapper vinpop-tasting-see-wrapper vinpop-discover-see-wrapper';

    var linkBtn = document.createElement('a');
    linkBtn.className = 'vinpop-tasting-cart-btn';
    linkBtn.href = '#';
    linkBtn.setAttribute('aria-disabled', 'true');
    linkBtn.innerHTML = '👀 See the real wine';

    var realWineUrl = '';

    linkBtn.addEventListener('click', function(e) {
      if (!realWineUrl) {
        e.preventDefault();
        e.stopPropagation();
      }
    });

    resolveRealWineHandle(cardProductId, function(handle) {
      if (handle) {
        var localeMatch = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i);
        var localePrefix = localeMatch ? '/' + localeMatch[1] : '';
        realWineUrl = localePrefix + '/products/' + handle;
        linkBtn.href = realWineUrl;
        linkBtn.style.pointerEvents = 'auto';
        linkBtn.style.cursor = 'pointer';
        linkBtn.removeAttribute('tabindex');
        linkBtn.removeAttribute('aria-disabled');
      } else {
        linkBtn.href = '#';
        linkBtn.setAttribute('aria-disabled', 'true');
        linkBtn.textContent = '❌ Wine not available';
      }
    });

    wrapper.appendChild(linkBtn);
    quickBuy.appendChild(wrapper);

    // Ensure our button appears BELOW the fav button
    var existingFav = quickBuy.querySelector('.vinpop-fav-btn-wrapper:not(.vinpop-tasting-cart-wrapper):not(.vinpop-tasting-see-wrapper)');
    if (existingFav) {
      quickBuy.appendChild(wrapper);
    } else {
      var obs = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1 &&
                node.classList.contains('vinpop-fav-btn-wrapper') &&
                !node.classList.contains('vinpop-tasting-cart-wrapper') &&
                !node.classList.contains('vinpop-tasting-see-wrapper')) {
              quickBuy.appendChild(wrapper);
              obs.disconnect();
            }
          });
        });
      });
      obs.observe(quickBuy, { childList: true });
    }
  }

  // ========== ADD SCORE BADGE (same SVG sticker style as /quizz) ==========
  function addScoreBadge(card, score) {
    var mainLink = card.querySelector('a[href*="/products/"]') ||
                   card.querySelector('.product-tile') ||
                   card.querySelector('.relative') ||
                   card;
    if (!mainLink) return;

    // Remove any existing badge
    var existing = mainLink.querySelector('.vinpop-score-badge');
    if (existing) existing.remove();

    var roundedScore = Math.round(score);
    var badgeContent = '<div style="line-height:1.05;text-align:center;"><span style="font-size:22px;font-weight:900;">' + roundedScore + '%</span><br><span style="font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-weight:700;">match</span></div>';

    var badge = document.createElement('div');
    badge.className = 'vinpop-score-badge pointer-events-none';
    badge.style.cssText = 'position:absolute;top:8px;right:8px;width:72px;height:72px;border-radius:50%;' +
      'background:#E07A5F;color:#fff;display:flex;align-items:center;justify-content:center;' +
      "text-align:center;font-family:'Prompt',sans-serif;" +
      'box-shadow:0 3px 10px rgba(0,0,0,0.18);' +
      'transform:rotate(5deg);' +
      'z-index:30;pointer-events:none;' +
      'border:2px solid rgba(0,0,0,0.15);';
    badge.innerHTML = badgeContent;

    mainLink.style.position = 'relative';
    mainLink.appendChild(badge);
  }

  // ========== GENERATE JUSTIFICATION ==========
  function generateTastingJustification(product) {
    try {
      const profileJson = sessionStorage.getItem(STORAGE_KEYS.TASTE_PROFILE);
      if (!profileJson) return null;

      const profile = JSON.parse(profileJson);
      const choices = profile.choices || {};
      if (Object.keys(choices).length === 0) return null;

      const isNL = (document.documentElement.lang || 'nl').toLowerCase().startsWith('nl');
      const reasons = [];

      const wineColor = (product.wineColor || product.type || '').toUpperCase();
      const isRed = wineColor.includes('R');
      const isWhite = wineColor.includes('W');

      if (choices.q1 === 0) {
        reasons.push(isRed
          ? (isNL ? '☕ Je houdt van zwarte koffie → deze rode wijn heeft dezelfde kracht' : '☕ You like black coffee → this red has that same boldness')
          : (isNL ? '☕ Je houdt van intense smaken → deze wijn heeft karakter' : '☕ You enjoy intense flavors → this wine has character'));
      } else if (choices.q1 === 1) {
        reasons.push(isNL ? '☕ Je kiest voor mild & romig → deze wijn is zacht en toegankelijk' : '☕ You prefer mild & creamy → this wine is smooth and approachable');
      } else if (choices.q1 === 2) {
        reasons.push(isNL ? '☕ Je houdt van lichte dranken → deze wijn is fris en delicaat' : '☕ You enjoy light drinks → this wine is fresh and delicate');
      }

      if (choices.q2 === 0) {
        reasons.push(isNL ? '🍫 Je kiest voor zoete chocolade → fruitige, zachte smaken passen bij jou' : '🍫 You choose sweet chocolate → fruity, soft flavors suit you');
      } else if (choices.q2 === 1) {
        reasons.push(isNL ? '🍫 Je kiest voor pure chocolade → complexe, gestructureerde wijnen passen bij jou' : '🍫 You choose dark chocolate → complex, structured wines suit you');
      } else if (choices.q2 === 2) {
        reasons.push(isNL ? '🍫 Je houdt van fruitige smaken → deze wijn heeft mooie fruittonen' : '🍫 You love fruity flavors → this wine has beautiful fruit notes');
      }

      if (choices.q3 === 0) {
        reasons.push(isNL ? '🥗 Je houdt van citroen & azijn → de frisse zuren in deze wijn zijn voor jou' : '🥗 You love lemon & vinegar → the crisp acidity in this wine is for you');
      } else if (choices.q3 === 1) {
        reasons.push(isNL ? '🥗 Je kiest voor romige dressings → deze wijn is rond en vol in de mond' : '🥗 You prefer creamy dressings → this wine is round and full-bodied');
      } else if (choices.q3 === 2) {
        reasons.push(isNL ? '🥗 Je houdt het graag simpel → deze wijn is puur en gebalanceerd' : '🥗 You prefer simplicity → this wine is pure and balanced');
      }

      if (choices.q4 === 0) {
        reasons.push(isNL ? '🧀 Je kiest voor zachte kazen → deze wijn is soepel en verfijnd' : '🧀 You prefer mild cheeses → this wine is supple and refined');
      } else if (choices.q4 === 1) {
        reasons.push(isNL ? '🧀 Je houdt van belegen kaas → deze wijn heeft diezelfde diepgang' : '🧀 You love aged cheese → this wine has that same depth');
      }

      if (choices.q5 === 0) {
        reasons.push(isNL ? '🎯 Je zocht iets fris & fruitig → deze wijn is precies dat' : '🎯 You wanted something fresh & fruity → this wine is exactly that');
      } else if (choices.q5 === 1) {
        reasons.push(isNL ? '🎯 Je zocht iets zacht & rond → deze wijn heeft die elegantie' : '🎯 You wanted something smooth & round → this wine has that elegance');
      } else if (choices.q5 === 2) {
        reasons.push(isNL ? '🎯 Je zocht iets krachtig & intens → deze wijn is precies dat' : '🎯 You wanted something bold & powerful → this wine is exactly that');
      }

      let selected = [];
      if (isRed && reasons.length > 0) {
        const priority = [0, 3, 4, 1, 2];
        for (const idx of priority) {
          if (reasons[idx] && selected.length < 2) selected.push(reasons[idx]);
        }
      } else if (isWhite && reasons.length > 0) {
        const priority = [2, 1, 4, 0, 3];
        for (const idx of priority) {
          if (reasons[idx] && selected.length < 2) selected.push(reasons[idx]);
        }
      }
      if (selected.length < 2) {
        selected = reasons.slice(0, 2);
      }

      if (selected.length === 0) {
        const score = product.score || 0;
        if (score >= 95) {
          selected.push(isNL ? '🎯 Perfecte match voor jouw smaakprofiel' : '🎯 Perfect match for your taste profile');
        } else if (score >= 90) {
          selected.push(isNL ? '🎯 Uitstekende match voor jouw smaak' : '🎯 Excellent match for your palate');
        } else {
          selected.push(isNL ? '🎯 Goede match op basis van jouw antwoorden' : '🎯 Great match based on your answers');
        }
      }

      return selected.map(r => `<span class="vinpop-justif-line">${r}</span>`).join('');
    } catch (e) {
      console.warn('⚠️ Could not generate tasting justification:', e);
      return null;
    }
  }

  // ========== COMPLETED STATE (inline quiz → retake button) ==========
  function getStoredResultsWines() {
    var raw = sessionStorage.getItem(STORAGE_KEYS.LAST_RESULTS) ||
              localStorage.getItem(STORAGE_KEYS.LAST_RESULTS_PERSIST);
    if (!raw) return null;

    try {
      var wines = JSON.parse(raw);
      if (!Array.isArray(wines) || wines.length === 0) return null;
      if (!sessionStorage.getItem(STORAGE_KEYS.LAST_RESULTS)) {
        sessionStorage.setItem(STORAGE_KEYS.LAST_RESULTS, JSON.stringify(wines));
      }
      return wines;
    } catch (e) {
      console.warn('⚠️ Invalid stored tasting results, clearing cache');
      sessionStorage.removeItem(STORAGE_KEYS.LAST_RESULTS);
      localStorage.removeItem(STORAGE_KEYS.LAST_RESULTS_PERSIST);
      return null;
    }
  }

  function showCompletedState(popupEl) {
    var isNL = (document.documentElement.lang || 'nl').toLowerCase().startsWith('nl');
    var questionsDiv = popupEl.querySelector('.taste-profile-questions');
    var progressDiv = popupEl.querySelector('.taste-profile-progress');
    if (progressDiv) progressDiv.style.display = 'none';
    // Collapse the bottom padding that creates empty space in completed state
    var contentDiv = popupEl.querySelector('.taste-profile-popup-content');
    if (contentDiv) contentDiv.style.paddingBottom = '0';
    if (questionsDiv) {
      questionsDiv.innerHTML =
        '<div style="text-align:center;padding:1.5rem 1rem;">' +
          '<p style="font-size:1.1rem;font-weight:700;margin-bottom:0.75rem;">' +
            (isNL ? '✅ Jouw selectie is klaar! Scroll naar beneden.' : '✅ Your selection is ready! Scroll down.') +
          '</p>' +
          '<button type="button" class="quiz-intro-start-btn push-btn" onclick="' +
            "localStorage.removeItem('vinpop-quizz-tasting-completed');" +
            "sessionStorage.removeItem('vinpop-quizz-tasting-completed');" +
            "sessionStorage.removeItem('vinpop_quizz_tasting_results');" +
            "localStorage.removeItem('vinpop_quizz_tasting_results_persist');" +
            "sessionStorage.removeItem('vinpop_quizz_tasting_profile');" +
            "sessionStorage.removeItem('vinpop_quizz_tasting_answers');" +
            "location.reload();" +
          '">' +
            '<span class="push-btn__surface">' + (isNL ? '🔄 Quiz opnieuw doen' : '🔄 Retake the Quiz') + '</span>' +
          '</button>' +
        '</div>';
    }
    console.log('🧪 Showing completed state with retake button');
  }

  // ========== AUTO-POPUP LOGIC ==========
  function setupAutoPopup() {
    const completed = localStorage.getItem(STORAGE_KEYS.QUIZ_COMPLETED) === 'true' ||
                      sessionStorage.getItem(STORAGE_KEYS.QUIZ_COMPLETED) === 'true';

    if (completed) {
      console.log('🧪 Quiz already completed, checking for stored results...');
      var wines = getStoredResultsWines();
      if (!wines) {
        console.warn('⚠️ Completed flag found without stored bottles. Resetting completed state.');
        localStorage.removeItem(STORAGE_KEYS.QUIZ_COMPLETED);
        sessionStorage.removeItem(STORAGE_KEYS.QUIZ_COMPLETED);
      } else {
        // Show completed state in inline quiz (retake button)
        var inlinePopup = document.querySelector('.taste-profile-popup--inline');
        if (inlinePopup) {
          showCompletedState(inlinePopup);
        }

        console.log('🧪 Restoring previous results:', wines.length, 'wines');
        renderTastingQuizResults(wines);
        return;
      }
    }

    // Quiz does NOT auto-open — user must click the trigger button
    console.log('🧪 Quiz ready, waiting for user to click trigger button');
  }

  // ========== CHECK FOR URL PARAMS ==========
  function checkForRestoreFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('taste_profile') === 'true') {
      const wines = getStoredResultsWines();
      if (wines) {
        console.log('🧪 Restoring results from URL param:', wines.length, 'wines');
        renderTastingQuizResults(wines);
        return true;
      }
    }
    return false;
  }

  function injectSignupRewardPopupStyles() {
    if (document.getElementById('vinpop-signup-reward-popup-styles')) return;

    var style = document.createElement('style');
    style.id = 'vinpop-signup-reward-popup-styles';
    style.textContent =
      '.vinpop-signup-popup-overlay{' +
        'position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;' +
        'padding:18px;background:rgba(22,18,15,.14);backdrop-filter:blur(6px);opacity:0;visibility:hidden;' +
        'transition:opacity .28s ease,visibility .28s ease;' +
      '}' +
      '.vinpop-signup-popup-overlay.is-visible{opacity:1;visibility:visible;}' +
      '.vinpop-signup-popup{' +
        'position:relative;max-width:560px;width:100%;overflow:hidden;border-radius:28px;' +
        'background:linear-gradient(145deg,#f6efe5 0%,#fffaf5 52%,#f2dcc8 100%);' +
        'border:1px solid rgba(120,71,31,.12);box-shadow:0 24px 80px rgba(63,37,18,.22);' +
        'transform:translateY(24px) scale(.97);transition:transform .34s cubic-bezier(.16,1,.3,1);' +
      '}' +
      '.vinpop-signup-popup-overlay.is-visible .vinpop-signup-popup{transform:translateY(0) scale(1);}' +
      '.vinpop-signup-popup__top{' +
        'padding:34px 34px 22px;background:radial-gradient(circle at top left, rgba(129,178,154,.34), transparent 42%),' +
        'radial-gradient(circle at top right, rgba(224,122,95,.26), transparent 34%);' +
      '}' +
      '.vinpop-signup-popup__title{' +
        'margin:0 0 12px;color:#23150d;font-size:clamp(28px,4vw,38px);line-height:1.02;font-weight:800;' +
      '}' +
      '.vinpop-signup-popup__perks{' +
        'display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;padding:0 34px;margin:0 0 26px;transform:translateY(-10px);' +
      '}' +
      '.vinpop-signup-popup__perk{' +
        'padding:16px 18px;border-radius:20px;background:rgba(255,255,255,.82);border:1px solid rgba(35,21,13,.08);' +
        'box-shadow:0 10px 26px rgba(63,37,18,.08);' +
      '}' +
      '.vinpop-signup-popup__perk-value{' +
        'display:block;margin-bottom:6px;color:#1f130d;font-size:26px;line-height:1;font-weight:800;' +
      '}' +
      '.vinpop-signup-popup__perk-label{' +
        'display:block;color:#6b5346;font-size:13px;line-height:1.45;' +
      '}' +
      '.vinpop-signup-popup__footer{' +
        'display:flex;gap:12px;align-items:center;justify-content:center;padding:0 34px 32px;flex-wrap:wrap;' +
      '}' +
      '.vinpop-signup-popup__actions{display:flex;gap:10px;flex-wrap:wrap;width:100%;justify-content:center;}' +
      '.vinpop-signup-popup__cta{' +
        'appearance:none;border:none;border-radius:999px;padding:14px 20px;font-size:14px;font-weight:700;cursor:pointer;' +
        'transition:transform .15s ease,box-shadow .15s ease,background .2s ease,color .2s ease;' +
      '}' +
      '.vinpop-signup-popup__cta{' +
        'background:#81B29A;color:#fff;box-shadow:0 12px 30px rgba(129,178,154,.28);' +
      '}' +
      '.vinpop-signup-popup__cta:hover,.vinpop-signup-popup__close:hover{' +
        'transform:translateY(-1px);' +
      '}' +
      '.vinpop-signup-popup__close{' +
        'position:absolute;top:16px;right:16px;width:38px;height:38px;border:none;border-radius:999px;' +
        'background:rgba(255,255,255,.82);color:#6b5346;font-size:20px;line-height:1;cursor:pointer;' +
      '}' +
      '@media (max-width: 640px){' +
        '.vinpop-signup-popup{border-radius:22px;}' +
        '.vinpop-signup-popup__top{padding:26px 22px 18px;}' +
        '.vinpop-signup-popup__perks{grid-template-columns:1fr;padding:0 22px;margin-bottom:20px;}' +
        '.vinpop-signup-popup__footer{padding:0 22px 24px;}' +
        '.vinpop-signup-popup__actions{width:100%;}' +
        '.vinpop-signup-popup__cta{width:100%;}' +
      '}';
    document.head.appendChild(style);
  }

  function showSignupRewardPopup() {
    var customerMeta = document.querySelector('meta[name="customer-id"]');
    if (!customerMeta) return;

    var signupSource = '';
    var rewardPending = '';
    try {
      signupSource = sessionStorage.getItem('vinpop_signup_source') || '';
      rewardPending = sessionStorage.getItem('vinpop_event_reward_pending') || '';
    } catch (e) {
      signupSource = '';
      rewardPending = '';
    }
    if (signupSource !== 'quizz-tasting' && rewardPending !== 'quizz-tasting') return;

    var customerId = customerMeta.getAttribute('content') || 'guest';
    var shownKey = 'vinpop_quizz_tasting_signup_reward_shown_' + customerId;
    try {
      if (sessionStorage.getItem(shownKey) === 'true') return;
    } catch (e) {}

    var isEN = (document.documentElement.lang || 'nl').toLowerCase().startsWith('en');
    var texts = isEN ? {
      title: 'Thanks for joining the tasting.',
      perkDiscountValue: '20% off',
      perkDiscountLabel: 'Valid for 48 hours after sign-in',
      perkShippingValue: 'Free delivery',
      perkShippingLabel: 'On every order above €29.90',
      cta: 'Let\'s start'
    } : {
      title: 'Bedankt voor je deelname aan de tasting.',
      perkDiscountValue: '20% korting',
      perkDiscountLabel: '48 uur geldig na het inloggen',
      perkShippingValue: 'Gratis levering',
      perkShippingLabel: 'Voor elke bestelling boven €29,90',
      cta: 'Laten we starten'
    };

    injectSignupRewardPopupStyles();

    var overlay = document.createElement('div');
    overlay.className = 'vinpop-signup-popup-overlay';
    overlay.innerHTML =
      '<div class="vinpop-signup-popup" role="dialog" aria-modal="true" aria-labelledby="vinpop-signup-popup-title">' +
        '<button type="button" class="vinpop-signup-popup__close" aria-label="Close">&times;</button>' +
        '<div class="vinpop-signup-popup__top">' +
          '<h2 id="vinpop-signup-popup-title" class="vinpop-signup-popup__title">' + texts.title + '</h2>' +
        '</div>' +
        '<div class="vinpop-signup-popup__perks">' +
          '<div class="vinpop-signup-popup__perk">' +
            '<span class="vinpop-signup-popup__perk-value">' + texts.perkDiscountValue + '</span>' +
            '<span class="vinpop-signup-popup__perk-label">' + texts.perkDiscountLabel + '</span>' +
          '</div>' +
          '<div class="vinpop-signup-popup__perk">' +
            '<span class="vinpop-signup-popup__perk-value">' + texts.perkShippingValue + '</span>' +
            '<span class="vinpop-signup-popup__perk-label">' + texts.perkShippingLabel + '</span>' +
          '</div>' +
        '</div>' +
        '<div class="vinpop-signup-popup__footer">' +
          '<div class="vinpop-signup-popup__actions">' +
            '<button type="button" class="vinpop-signup-popup__cta">' + texts.cta + '</button>' +
          '</div>' +
        '</div>' +
      '</div>';

    function cleanupStorage() {
      try {
        sessionStorage.setItem(shownKey, 'true');
        sessionStorage.removeItem('vinpop_signup_source');
        sessionStorage.removeItem('vinpop_event_reward_pending');
      } catch (e) {}
    }

    function closePopup() {
      overlay.classList.remove('is-visible');
      setTimeout(function() {
        overlay.remove();
      }, 280);
    }

    function focusResults() {
      var target = document.querySelector('[data-product-grid]') ||
        document.querySelector('#MainContent .shopify-section') ||
        document.querySelector('main');
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    cleanupStorage();
    document.body.appendChild(overlay);

    var closeBtn = overlay.querySelector('.vinpop-signup-popup__close');
    var ctaBtn = overlay.querySelector('.vinpop-signup-popup__cta');

    requestAnimationFrame(function() {
      overlay.classList.add('is-visible');
    });

    closeBtn.addEventListener('click', closePopup);
    ctaBtn.addEventListener('click', function() {
      closePopup();
      focusResults();
    });
    overlay.addEventListener('click', function(event) {
      if (event.target === overlay) {
        closePopup();
      }
    });
    document.addEventListener('keydown', function handler(event) {
      if (event.key === 'Escape') {
        closePopup();
        document.removeEventListener('keydown', handler);
      }
    });
  }

  // ========== MAIN INITIALIZATION ==========
  function initQuizzTasting() {
    // Hijack existing quiz elements (redirect trigger button, replace banner)
    // KEEP the #taste-profile-popup — we reuse it!
    try {
      hijackExistingQuiz();
    } catch (err) {
      console.error('🧪 hijackExistingQuiz ERROR:', err);
    }

    try {
      showSignupRewardPopup();
    } catch (err) {
      console.error('ðŸ§ª showSignupRewardPopup ERROR:', err);
    }

    // Create quiz instance using existing #taste-profile-popup
    window.tastingQuiz = new TastingQuiz();

    // Check if we're restoring results
    const restoredFromURL = checkForRestoreFromURL();

    if (!restoredFromURL) {
      const wines = getStoredResultsWines();
      const completed = localStorage.getItem(STORAGE_KEYS.QUIZ_COMPLETED) === 'true' ||
                        sessionStorage.getItem(STORAGE_KEYS.QUIZ_COMPLETED) === 'true';

      if (completed && wines) {
        console.log('🧪 Re-rendering stored results:', wines.length, 'wines');
        renderTastingQuizResults(wines);
      } else {
        setupAutoPopup();
      }
    }
  }

  // ========== BOOTSTRAP ==========
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuizzTasting);
  } else {
    initQuizzTasting();
  }

})();
